import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray, record, string } from 'fp-ts';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { LocalizedText, lt, NumberService, showSuccessAlert, UseDialogActionReturn } from '@mews/framework';
import { useLanguageCode } from '@mews-commander/core';
import { TextKey } from '@mews-commander/enums';
import {
    BaseRatePricing,
    DependentRatePricing,
    PortfolioRateOverview,
    RatePricingDiscriminator,
} from '@mews-commander/data-access';
import { ApplicationState } from '@mews-commander/types';
import { getCurrentNumberCultureInfo } from '@mews-commander/core/legacy-globalization';
import { mergeWithDefaultValues, separateByUniformValues } from '../../../../utilities';
import { checkIfSomeEntitiesAreFromSameEnterprise } from '../../../../utils';
import { FormErrors } from '../../../../types';
import { useEnterprisesBusinessSegments, useRateByIds, useRateEdit } from '../../../../queries';
import { FieldName, FieldsConfig } from './fields/types';
import { getBulkEditRatesValues, setFieldsConfig } from './helpers';
import { DEFAULT_VALUES, RateContainer } from './RateContainer';
import {
    RATE_TYPE_FIELD_NAME,
    NAME_FIELD_NAME,
    SHORT_NAME_FIELD_NAME,
    EXTERNAL_NAME_FIELD_NAME,
    DESCRIPTION_FIELD_NAME,
    RATE_KEY_FIELD_NAME,
    RATE_GROUP_KEY_FIELD_NAME,
    ENABLED_FIELD_NAME,
    IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
    HIDE_PRICE_FROM_GUEST_FIELD_NAME,
    BASE_PRICE_FIELD_NAME,
    BASE_RATE_KEY_FIELD_NAME,
    ABSOLUTE_ADJUSTMENT_FIELD_NAME,
    RELATIVE_ADJUSTMENT_FIELD_NAME,
    BUSINESS_SEGMENT_KEY_FIELD_NAME,
    ACCOUNTING_CATEGORY_KEY_FIELD_NAME,
    EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
    ORDERING_FIELD_NAME,
} from './fields';
import { FormValues, PreparedValues } from './types';
import { useAccountingCategories } from './fields/accountingCategoriesKey/useAccountingCategories';
import { useRateFormContext } from './hooks/RateFormProvider';

interface Props extends UseDialogActionReturn {
    areRatesDependent: boolean;
    selectedRateIds: string[];
    selectedRateOverviews?: PortfolioRateOverview[];
    onSuccess?: () => void;
}

export const EditRateContainer = ({
    areRatesDependent,
    states,
    handlers,
    selectedRateIds,
    selectedRateOverviews,
    onSuccess,
}: Props) => {
    const { rateResults } = useRateFormContext();

    const { mutate, isPending } = useRateEdit();
    const [formErrors, setFormErrors] = useState<FormErrors>();
    const [unlockedFields, setUnlockedFields] = useState<FieldName[]>([]);

    const unlockField = (fieldName: FieldName) => {
        setUnlockedFields((prev) => [...prev, fieldName]);
    };

    const numberCultureInfo = useSelector((state: ApplicationState) => getCurrentNumberCultureInfo(state));
    numberCultureInfo.groupSeparator = ''; // Here we only want to format decimal only.

    const serviceIds = rateResults?.rates ? rateResults?.rates.map((rate) => rate.serviceId) : [];

    const selectedEnterpriseIds = pipe(
        rateResults?.rates || [],
        array.map(({ enterpriseId }) => enterpriseId),
        array.uniq(string.Eq)
    );

    const { data: businessSegmentResults } = useEnterprisesBusinessSegments(selectedEnterpriseIds, serviceIds);
    const { data: accountingCategoriesResults } = useAccountingCategories(selectedEnterpriseIds);

    const languageCode = useLanguageCode();

    const { data: baseRateData } = useRateByIds(
        selectedEnterpriseIds,
        rateResults?.rates?.[0].baseRateId ? [rateResults?.rates?.[0].baseRateId] : undefined,
        {
            enabled: areRatesDependent && selectedRateIds.length === 1 && !!rateResults?.rates?.[0],
        }
    );

    if (!rateResults || !rateResults?.rates || !rateResults?.rateGroups) {
        return null;
    }

    const baseRateKey = baseRateData?.rates?.[0].externalIdentifier;

    const businessSegmentsKeysByIds = pipe(
        businessSegmentResults?.businessSegments || [],
        nonEmptyArray.groupBy((businessSegment) => businessSegment.id),
        record.map((businessSegments) => businessSegments[0].externalIdentifier)
    );

    const accountingCategoriesByIds = pipe(
        accountingCategoriesResults?.accountingCategories || [],
        nonEmptyArray.groupBy((accountingCategory) => accountingCategory.id),
        record.map((accountingCategories) => accountingCategories[0].externalIdentifier)
    );

    const ratesFormValues = pipe(
        rateResults.rates,
        array.map((rate) => {
            const {
                name,
                shortName,
                type,
                description,
                externalNames,
                externalIdentifier,
                isEnabled,
                options,
                ordering,
            } = rate;

            const pricing = rateResults.pricings?.find((ratePricing) => ratePricing.rateId === rate.id)?.pricing;
            const rateGroupKeyExternalIdentifier = rateResults.rateGroups?.find(
                (rateGroup) => rateGroup.id === rate.rateGroupId
            )?.externalIdentifier;

            return {
                [RATE_TYPE_FIELD_NAME]: type,
                [NAME_FIELD_NAME]: name,
                [SHORT_NAME_FIELD_NAME]: shortName,
                [DESCRIPTION_FIELD_NAME]: description,
                [ENABLED_FIELD_NAME]: Number(isEnabled),
                [EXTERNAL_NAME_FIELD_NAME]: externalNames[languageCode],
                [RATE_KEY_FIELD_NAME]: externalIdentifier,
                [IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME]: options.isBonusPointsEligible,
                [HIDE_PRICE_FROM_GUEST_FIELD_NAME]: options.hidePriceFromGuest,
                [BASE_RATE_KEY_FIELD_NAME]: baseRateKey,
                [BASE_PRICE_FIELD_NAME]: (pricing?.value as BaseRatePricing).value,
                [RATE_GROUP_KEY_FIELD_NAME]: rateGroupKeyExternalIdentifier,
                [ABSOLUTE_ADJUSTMENT_FIELD_NAME]: (pricing?.value as DependentRatePricing).absoluteValue,
                [RELATIVE_ADJUSTMENT_FIELD_NAME]: (pricing?.value as DependentRatePricing).relativeValue,
                [BUSINESS_SEGMENT_KEY_FIELD_NAME]: rate.businessSegmentId
                    ? businessSegmentsKeysByIds[rate.businessSegmentId]
                    : null,
                [ACCOUNTING_CATEGORY_KEY_FIELD_NAME]: rate.accountingCategoryId
                    ? accountingCategoriesByIds[rate.accountingCategoryId]
                    : null,
                [EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: (pricing?.value as BaseRatePricing).extraOccupancyAdjustment,
                [NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME]: (pricing?.value as BaseRatePricing)
                    .negativeOccupancyAdjustment,
                [ORDERING_FIELD_NAME]: ordering,
            };
        })
    );

    const fields: FieldName[] = [
        RATE_TYPE_FIELD_NAME,
        NAME_FIELD_NAME,
        SHORT_NAME_FIELD_NAME,
        EXTERNAL_NAME_FIELD_NAME,
        DESCRIPTION_FIELD_NAME,
        RATE_KEY_FIELD_NAME,
        RATE_GROUP_KEY_FIELD_NAME,
        ENABLED_FIELD_NAME,
        IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME,
        HIDE_PRICE_FROM_GUEST_FIELD_NAME,
        BASE_PRICE_FIELD_NAME,
        ABSOLUTE_ADJUSTMENT_FIELD_NAME,
        RELATIVE_ADJUSTMENT_FIELD_NAME,
        BUSINESS_SEGMENT_KEY_FIELD_NAME,
        ACCOUNTING_CATEGORY_KEY_FIELD_NAME,
        EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
        NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME,
        ORDERING_FIELD_NAME,
    ];

    const { left: lockedFields, right: enabledFields } = separateByUniformValues(ratesFormValues, fields);
    const areSomeRatesFromSameEnterprise = checkIfSomeEntitiesAreFromSameEnterprise(rateResults?.rates);

    const fieldsConfig: FieldsConfig = setFieldsConfig(
        enabledFields,
        lockedFields,
        areRatesDependent,
        areSomeRatesFromSameEnterprise
    );

    const firstRate = rateResults.rates[0];
    const firstRateOverview = selectedRateOverviews?.[0];
    const firstRatePricing = rateResults.pricings?.find((pricing) => pricing.rateId === firstRate.id);
    const firstRateGroup = rateResults.rateGroups?.find((rateGroup) => rateGroup.id === firstRate.rateGroupId);

    const { type, isEnabled, description, externalIdentifier, ordering } = firstRate;
    const name = firstRateOverview?.name ?? firstRate.name;
    const shortName = firstRateOverview?.shortName ?? firstRate.shortName;
    const externalName = firstRateOverview?.externalName ?? firstRate.externalNames[languageCode];
    const businessSegmentKey =
        (firstRate.businessSegmentId && businessSegmentsKeysByIds[firstRate.businessSegmentId]) || '';
    const accountingCategoryKey =
        (firstRate?.accountingCategoryId && accountingCategoriesByIds[firstRate.accountingCategoryId]) || '';

    const { isBonusPointsEligible, hidePriceFromGuest } = firstRate.options;

    const values: FormValues = {
        // Common Fields
        // @ts-expect-error Rate type name should be empty string if rate type is not set.
        [RATE_TYPE_FIELD_NAME]: enabledFields.includes(RATE_TYPE_FIELD_NAME) ? type : '',
        [NAME_FIELD_NAME]: enabledFields.includes(NAME_FIELD_NAME) ? name : '',
        [SHORT_NAME_FIELD_NAME]: enabledFields.includes(SHORT_NAME_FIELD_NAME) ? shortName : '',
        [EXTERNAL_NAME_FIELD_NAME]: enabledFields.includes(EXTERNAL_NAME_FIELD_NAME) ? externalName : '',
        [ENABLED_FIELD_NAME]: enabledFields.includes(ENABLED_FIELD_NAME) ? Number(isEnabled) : 0,
        [DESCRIPTION_FIELD_NAME]: enabledFields.includes(DESCRIPTION_FIELD_NAME) && description ? description : '',
        [RATE_GROUP_KEY_FIELD_NAME]: enabledFields.includes(RATE_GROUP_KEY_FIELD_NAME)
            ? firstRateGroup?.externalIdentifier || ''
            : '',
        [RATE_KEY_FIELD_NAME]:
            enabledFields.includes(RATE_KEY_FIELD_NAME) && externalIdentifier ? externalIdentifier : '',
        [IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME]: enabledFields.includes(IS_BONUS_POINTS_ELIGIBLE_FIELD_NAME)
            ? isBonusPointsEligible
            : false,
        [HIDE_PRICE_FROM_GUEST_FIELD_NAME]: enabledFields.includes(HIDE_PRICE_FROM_GUEST_FIELD_NAME)
            ? hidePriceFromGuest
            : false,
        [BUSINESS_SEGMENT_KEY_FIELD_NAME]: enabledFields.includes(BUSINESS_SEGMENT_KEY_FIELD_NAME)
            ? businessSegmentKey
            : '',
        [ACCOUNTING_CATEGORY_KEY_FIELD_NAME]: enabledFields.includes(ACCOUNTING_CATEGORY_KEY_FIELD_NAME)
            ? accountingCategoryKey
            : '',
        [ORDERING_FIELD_NAME]: enabledFields.includes(ORDERING_FIELD_NAME) ? ordering?.toString() : '',

        // Base Rate Fields
        [BASE_PRICE_FIELD_NAME]:
            enabledFields.includes(BASE_PRICE_FIELD_NAME) &&
            firstRatePricing?.pricing?.discriminator === RatePricingDiscriminator.BaseRate
                ? NumberService.format(firstRatePricing.pricing.value.value, numberCultureInfo).toString()
                : '',
        [EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME]:
            enabledFields.includes(EXTRA_OCCUPANCY_ADJUSTMENT_FIELD_NAME) &&
            firstRatePricing?.pricing?.discriminator === RatePricingDiscriminator.BaseRate &&
            firstRatePricing.pricing.value.extraOccupancyAdjustment != null
                ? NumberService.format(
                      firstRatePricing.pricing.value.extraOccupancyAdjustment,
                      numberCultureInfo
                  ).toString()
                : '',
        [NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME]:
            enabledFields.includes(NEGATIVE_OCCUPANCY_ADJUSTMENT_FIELD_NAME) &&
            firstRatePricing?.pricing?.discriminator === RatePricingDiscriminator.BaseRate &&
            firstRatePricing.pricing.value.negativeOccupancyAdjustment != null
                ? NumberService.format(
                      firstRatePricing.pricing.value.negativeOccupancyAdjustment,
                      numberCultureInfo
                  ).toString()
                : '',

        // Dependent Rate Fields
        [BASE_RATE_KEY_FIELD_NAME]: baseRateKey || '',
        [RELATIVE_ADJUSTMENT_FIELD_NAME]:
            enabledFields.includes(RELATIVE_ADJUSTMENT_FIELD_NAME) &&
            firstRatePricing?.pricing?.discriminator === RatePricingDiscriminator.DependentRate
                ? NumberService.formatPercent(firstRatePricing.pricing.value.relativeValue, numberCultureInfo, {
                      showSymbol: false,
                  }).toString()
                : '',
        [ABSOLUTE_ADJUSTMENT_FIELD_NAME]:
            enabledFields.includes(ABSOLUTE_ADJUSTMENT_FIELD_NAME) &&
            firstRatePricing?.pricing?.discriminator === RatePricingDiscriminator.DependentRate
                ? NumberService.format(firstRatePricing.pricing.value.absoluteValue, numberCultureInfo).toString()
                : '',
    };

    const selectedRatesCount = selectedRateIds.length;
    const handleSubmit = (preparedValues: PreparedValues, formValues: FormValues) => {
        // @ts-expect-error Argument of type 'FormValues' is not assignable to parameter of type 'Record<string, unknown>'
        const initialValues = mergeWithDefaultValues(values, DEFAULT_VALUES);
        const modifiedValuesKeys = pipe(
            // @ts-expect-error Argument of type 'FormValues' is not assignable to parameter of type 'Record<keyof FormValues, unknown>'
            record.keys(formValues),
            array.filter((key) => unlockedFields.includes(key) || formValues[key] !== initialValues[key])
        );

        mutate(
            {
                ...getBulkEditRatesValues({ formValues, preparedValues, modifiedValuesKeys }),
                rateIds: selectedRateIds,
            },
            {
                onSuccess: async () => {
                    showSuccessAlert({
                        title: lt('RatesEditedMessage', {
                            numberOfRates: selectedRatesCount,
                            rateName: formValues.name,
                        }),
                    });
                    await handlers.handleConfirm();

                    onSuccess?.();
                },
                onError: (e) => {
                    if (e.message.includes('rate key')) {
                        setFormErrors({
                            field: RATE_KEY_FIELD_NAME,
                            message: e.message,
                        });
                    }
                },
            }
        );
    };

    return (
        <RateContainer
            formErrors={formErrors}
            enterpriseIds={selectedEnterpriseIds}
            values={values}
            active={states.isDialogOpened}
            unlockField={unlockField}
            unlockedFields={unlockedFields}
            onCancel={handlers.handleCancel}
            onSubmit={handleSubmit}
            dependent={areRatesDependent}
            title={<LocalizedText textKey={selectedRatesCount === 1 ? TextKey.EditRate : TextKey.EditRatesInBulk} />}
            submitButtonTextKey={TextKey.Save}
            fieldsConfig={fieldsConfig}
            selectedRateIds={selectedRateIds}
            isLoading={isPending}
        />
    );
};
